import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import clsx from 'clsx'

import { Box, FormHelperText, FormLabel, Grid } from '@material-ui/core'

import { DefaultSelect } from './DefaultSelect'
import { SyncStartTableRow } from './SyncStartTableRow'
import { MoySkladSyncSwitch } from './MoySkladSyncSwitch'
import { WbMultiProductSwitch } from './WbMultiProductSwitch'
import { OzonMultiProductSwitch } from './OzonMultiProductSwitch'
import { MoySkladUseNumerationRow } from './MoySkladUseNumerationRow'
import { YandexMarketMultiProductSwitch } from './YandexMarketMultiProductSwitch'

import { useMessages } from '../hooks/snackbarHooks'
import { useApi, useStore } from '../hooks/storeHook'
import { useSettingsPageStyles } from '../hooks/settingsPageStylesHook'

import { emDash } from '../common/stringUtility'
import { valuesOfNumericEnum } from '../common/tsUtils'

import { moySkladStockTypeNames } from '../types/moySkladStockTypeNames'
import { MoySkladStockType } from '../server/mpsklad_core/Entity/Base/MoySkladStockType'

export const MoySkladSyncInfo =
  observer(
    () => {
      const classes = useSettingsPageStyles()

      const {showSuccess, showInfo} = useMessages()
      const {syncStore: {moySkladAccount}} = useStore()
      const {userSync: {setMoySkladStockType, setMoySkladSyncStartDate}} = useApi()

      const [stockType, setStockType] =
        useState(moySkladAccount?.stockType ?? MoySkladStockType.freeStock)

      const onStockTypeChange =
        async (value: MoySkladStockType | null | undefined) => {
          if (!moySkladAccount) {
            return
          }

          const newStockType = value ?? MoySkladStockType.freeStock
          setStockType(newStockType)

          try {
            await setMoySkladStockType({stockType: newStockType})
            moySkladAccount.stockType = newStockType
            showSuccess('Вариант учёта остатков сохранён!')
            showInfo('Запущен процесс обновления товаров')
          } catch (e) {
            // Rollback
            setStockType(moySkladAccount.stockType)
            console.error('Failed to update stock type:', e)
          }
        }

      if (!moySkladAccount) {
        return (
          <Box padding={3}>
            Подключите аккаунт МоегоСклада для синхронизации товаров.
          </Box>
        )
      }

      return (
        <Box className={classes.pageContainer}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                <Grid item className={classes.root} xs={12}>
                  <Box width={'100%'} display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <FormLabel className={classes.unifiedPricesLabel}>
                        Синхронизировать
                      </FormLabel>
                    </Box>

                    <MoySkladSyncSwitch/>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                <Grid item className={classes.root} xs={12}>
                  <Box width={'100%'} display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <FormLabel className={classes.unifiedPricesLabel}>
                        Дата начала синхронизации заказов
                      </FormLabel>

                      <FormHelperText className={classes.helperText}>
                        Будут синхронизированы заказы, созданные позже этой даты.
                      </FormHelperText>
                    </Box>
                  </Box>

                  <Box marginTop={2}>
                    <SyncStartTableRow account={moySkladAccount} onSubmit={setMoySkladSyncStartDate}/>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                <Grid item className={classes.root} xs={12}>
                  <Box width={'100%'} display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <FormLabel className={classes.unifiedPricesLabel}>
                        Использовать нумерацию МоегоСклада
                      </FormLabel>

                      <FormHelperText className={classes.helperText}>
                        Использовать номера заказов и отгрузок МоегоСклада вместо номеров Маркетплейса.
                      </FormHelperText>
                    </Box>

                    <MoySkladUseNumerationRow/>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                <Grid item className={classes.root} xs={12}>
                  <Box width={'100%'} display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <FormLabel className={classes.unifiedPricesLabel}>
                        Разрешить привязку нескольких товаров Ozon
                      </FormLabel>
                    </Box>

                    <OzonMultiProductSwitch/>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                <Grid item className={classes.root} xs={12}>
                  <Box width={'100%'} display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <FormLabel className={classes.unifiedPricesLabel}>
                        Разрешить привязку нескольких товаров Wildberries
                      </FormLabel>
                    </Box>

                    <WbMultiProductSwitch/>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                <Grid item className={classes.root} xs={12}>
                  <Box width={'100%'} display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <FormLabel className={classes.unifiedPricesLabel}>
                        Разрешить привязку нескольких товаров Yandex.Market
                      </FormLabel>
                    </Box>

                    <YandexMarketMultiProductSwitch/>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                <Grid item className={classes.root} xs={12}>
                  <Box>
                    <FormLabel component="legend" className={classes.boldLabel}>
                      Вариант учёта остатков
                    </FormLabel>
                  </Box>

                  <Box>
                    <DefaultSelect
                      fullWidth
                      placeholder={emDash}
                      value={stockType}
                      options={
                        valuesOfNumericEnum(MoySkladStockType).map(value => ({
                          value,
                          label: moySkladStockTypeNames[value]
                        }))}
                      onChange={onStockTypeChange}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      )
    })